import { Fund } from '../api/useFunds';
import {
  CB_50_REFUND,
  CB_100_FEE,
  DEFAULT_BIC,
  CB_80_REFUND,
  CashbackModel,
} from '../utils/cashbackConstants';

const CALCULATOR_DATA_KEY = 'fund-detail-calculator-data';
const PREFERRED_BIC_KEY = 'fund-detail-calculator-preferred-bic';

interface CacheRecord {
  fund: Fund;
  investedAmount: number;
}

export const calculateCashback = (
  fundsMap: Map<Fund, number>,
  bic: Bic | 'OTHER_BANK',
  model: CashbackModel = 'flat-fee'
) => {
  let totalAgio = 0;
  let totalPortfolioCommission = 0;
  let totalInvestedAmount = 0;
  const compareBic = bic === 'OTHER_BANK' ? 'COBADEHDXXX' : bic;

  fundsMap.forEach((amount, fund) => {
    const courtage = fund.courtages?.find((c) => {
      return c.bic === compareBic;
    });
    const investedAmount = Number(amount) || 0;

    if (investedAmount) {
      totalInvestedAmount += investedAmount;
      totalAgio += courtage?.agio
        ? courtage.agio * investedAmount * (courtage?.discountOnAgio || 0)
        : 0;
      totalPortfolioCommission += courtage?.portfolioCommission
        ? courtage.portfolioCommission * investedAmount
        : 0;
    }
  });

  const cashback = getCashback(totalPortfolioCommission, model);
  const fee = getFee(totalPortfolioCommission, model);

  return {
    totalInvestedAmount: totalInvestedAmount,
    cashback: cashback,
    totalPortfolioCommission: totalPortfolioCommission,
    totalPortfolioCommissionInPercent: totalInvestedAmount
      ? totalPortfolioCommission / totalInvestedAmount
      : 0,
    totalAgio: totalAgio,
    fee: fee,
  };
};

export const getCashback = (
  totalPortfolioCommission: number,
  model: CashbackModel = 'flat-fee'
) => {
  if (model === '80-percent') {
    return Math.round(totalPortfolioCommission * CB_80_REFUND);
  }
  return totalPortfolioCommission * CB_50_REFUND > totalPortfolioCommission - CB_100_FEE
    ? Math.round(totalPortfolioCommission * CB_50_REFUND)
    : Math.round(totalPortfolioCommission - CB_100_FEE);
};

export const getFee = (totalPortfolioCommission: number, model: CashbackModel = 'flat-fee') => {
  if (model === '80-percent') {
    return Math.round(totalPortfolioCommission * (1 - CB_80_REFUND));
  }
  return totalPortfolioCommission * CB_50_REFUND > totalPortfolioCommission - CB_100_FEE
    ? Math.round(totalPortfolioCommission * CB_50_REFUND)
    : Math.round(CB_100_FEE);
};

export const mapToCache = (fundsMap: Map<Fund, number>, bic: Bic) => {
  try {
    const cache: CacheRecord[] = [];
    fundsMap.forEach((investedAmount, fund) => {
      if (investedAmount) {
        cache.push({ fund, investedAmount: Number(investedAmount) || 0 });
      }
    });
    localStorage.setItem(CALCULATOR_DATA_KEY, JSON.stringify(cache));
    localStorage.setItem(PREFERRED_BIC_KEY, bic);
  } catch (err) {
    console.error(err);
  }
};

export const cacheToMap = () => {
  try {
    const map: Map<Fund, number> = new Map<Fund, number>();
    const item = localStorage.getItem(CALCULATOR_DATA_KEY);
    if (!item) {
      return map;
    }
    const cachedFunds: CacheRecord[] = JSON.parse(item);
    cachedFunds?.forEach((cacheRecord) => {
      map.set(cacheRecord.fund, Number(cacheRecord.investedAmount));
    });
    return map;
  } catch (err) {
    console.error(err);
    return new Map<Fund, number>();
  }
};

export const bicFromCache = () => {
  try {
    return localStorage.getItem(PREFERRED_BIC_KEY) || DEFAULT_BIC;
  } catch (err) {
    console.error(err);
    return DEFAULT_BIC;
  }
};
